import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import JsonData from "../../data/data.json";
import { navigateToSection } from "../../utils/hashSectionNav";
import { scrollToSectionId } from "../../utils/scrollToSection";
import { LanguageSwitcher } from "../LanguageSwitcher";
import { ProsegLockup } from "./prosegMark";
import { Ico } from "./prosegIcons";
import { useMediaQuery, useScrolled } from "./prosegMotion";
import "./proseg.css";

/** Seções da landing na ordem do menu (id → chave de tradução). */
export const NAV_SECTIONS = [
  { id: "cartao", key: "nav.card" },
  { id: "pix", key: "nav.pix" },
  { id: "seguranca", key: "nav.security" },
  { id: "empresas", key: "nav.business" },
  { id: "faq", key: "nav.faq" },
];

/** Largura a partir da qual o topo mostra o menu hambúrguer (mesma do CSS). */
export const NAV_NARROW_QUERY = "(max-width: 959px)";

/** URLs do app (login / cadastro / lojas) a partir de data.json. */
export function getProsegUrls() {
  const contact = JsonData.Contact || {};
  const domain = (contact.nameBankDomain || "").toLowerCase();
  const register = domain ? `https://app.${domain}.com.br/register` : "/#aplicativo";
  const login = domain ? `https://app.${domain}.com.br/login` : "/#aplicativo";
  return {
    login,
    register,
    appStore: contact.appStore || register,
    googlePlay: contact.googlePlay || register,
  };
}

/** Scroll para uma seção da landing (compensa a topbar fixa #menu). */
export function scrollToProsegSection(id) {
  if (id === "inicio") {
    window.scrollTo({ top: 0, behavior: "smooth" });
    return;
  }
  scrollToSectionId(id);
}

/**
 * Topo fixo do design, usado em todas as rotas:
 * - na landing: transparente no topo e sólido com blur ao rolar;
 * - nas páginas internas: sempre sólido;
 * - abaixo de 960px vira menu hambúrguer (com o seletor de idioma dentro).
 */
export const ProsegNav = () => {
  const { t } = useTranslation("prosegLanding");
  const navigate = useNavigate();
  const location = useLocation();
  const onLanding = location.pathname === "/";
  const scrolled = useScrolled(24);
  const narrow = useMediaQuery(NAV_NARROW_QUERY);
  const [open, setOpen] = useState(false);
  const burgerRef = useRef(null);
  const urls = getProsegUrls();

  /** Fecha o menu mobile; `restoreFocus` devolve o foco ao hambúrguer. */
  const closeMenu = useCallback((restoreFocus) => {
    setOpen(false);
    if (restoreFocus && burgerRef.current) burgerRef.current.focus();
  }, []);

  useEffect(() => {
    if (!narrow) setOpen(false);
  }, [narrow]);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") closeMenu(true);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, closeMenu]);

  const goTo = (id) => {
    if (open) closeMenu(true);
    if (onLanding) scrollToProsegSection(id);
    else navigateToSection(navigate, location, id);
  };

  const sectionLink = (id, label, className) => (
    <a
      key={id}
      href={`/#${id}`}
      className={className}
      onClick={(e) => {
        e.preventDefault();
        goTo(id);
      }}
    >
      {label}
    </a>
  );

  const solid = scrolled || !onLanding || open;
  const menuOpen = open && narrow;

  return (
    <>
      <header
        id="menu"
        className={`pp-nav${solid ? " is-solid" : ""}${onLanding ? "" : " is-inner"}`}
      >
        <a
          href="/#inicio"
          className="pp-nav-brand"
          onClick={(e) => {
            e.preventDefault();
            goTo("inicio");
          }}
        >
          <ProsegLockup eager />
        </a>

        <nav className="pp-nav-links" aria-label={t("nav.ariaMain")}>
          {NAV_SECTIONS.map((s) => sectionLink(s.id, t(s.key)))}
        </nav>

        <div className="pp-nav-actions">
          <a className="pp-nav-btn pp-nav-btn--ghost" href={urls.login} target="_blank" rel="noreferrer">
            {t("nav.signIn")}
          </a>
          <a
            className="pp-nav-btn pp-nav-btn--primary"
            href={urls.register}
            target="_blank"
            rel="noreferrer"
          >
            {t("nav.openAccount")}
          </a>
          <ul className="pp-nav-lang">
            <LanguageSwitcher />
          </ul>
          <button
            ref={burgerRef}
            type="button"
            className="pp-nav-burger"
            aria-label={open ? t("nav.closeMenu") : t("nav.menu")}
            aria-expanded={menuOpen}
            aria-controls={menuOpen ? "pp-mobile-menu" : undefined}
            onClick={() => (open ? closeMenu(false) : setOpen(true))}
          >
            <Ico name={open ? "close" : "menu"} size={22} sw={2} />
          </button>
        </div>
      </header>

      {menuOpen ? (
        <nav id="pp-mobile-menu" className="pp-nav-menu" aria-label={t("nav.ariaMain")}>
          {NAV_SECTIONS.map((s) => sectionLink(s.id, t(s.key)))}
          <a
            href={urls.login}
            className="is-cyan"
            target="_blank"
            rel="noreferrer"
            onClick={() => closeMenu(true)}
          >
            {t("nav.signIn")}
          </a>
          <div className="pp-nav-menu-lang">
            <span>{t("nav.language")}</span>
            <ul className="pp-nav-lang">
              <LanguageSwitcher />
            </ul>
          </div>
        </nav>
      ) : null}
    </>
  );
};
